import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../api";

/**
 * Generate and re-download invoices for one page/milestone (super admin only).
 *
 * An invoice is frozen when it's generated: the server stores the rendered PDF
 * and the exact details it printed, so every past one stays downloadable as it
 * was sent. That's why this is a list that grows rather than a single
 * "download invoice" button — generating again adds a new numbered document,
 * it never rewrites an old one.
 *
 * The amount is never typed here: it's the page total, computed server-side,
 * so an invoice can't disagree with the milestone the client already sees.
 */
const MONTHS_ABBR = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const ordinal = (n) => {
  if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
  return `${n}${{ 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th"}`;
};

/** Mirrors invoicing.format_period on the server, so the preview matches the
 *  PDF exactly. Dates are the yyyy-mm-dd strings a date input gives back. */
const formatPeriod = (from, to) => {
  const part = (iso) => {
    const [, m, d] = iso.split("-").map(Number);
    return `${MONTHS_ABBR[m - 1]}. ${ordinal(d)}`;
  };
  if (!to) return "";
  if (!from || from >= to) return part(to);
  return `${part(from)} – ${part(to)}`;
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleDateString();
};

export default function InvoiceModal({ open, sheetId, onClose }) {
  const [defaults, setDefaults] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [form, setForm] = useState({
    issued_on: "",
    period_from: "",
    period_to: "",
    description: "",
  });
  // Billing something other than the page total is rare and deliberate, so it
  // stays folded away until asked for.
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [amountOverride, setAmountOverride] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) {
      setErr(null);
      setOverrideOpen(false);
      setAmountOverride("");
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [d, list] = await Promise.all([
          api.get(`/sheets/${sheetId}/invoice-defaults`),
          api.get(`/sheets/${sheetId}/invoices`),
        ]);
        if (cancelled) return;
        setDefaults(d.data);
        setInvoices(list.data);
        // The period boxes start at the milestone's own date and today, which
        // is the usual answer; a page whose name carries no date leaves "from"
        // empty to fill in. They're starting values, not what prints: the PDF
        // shows whatever dates are in these boxes when you generate.
        setForm({
          issued_on: d.data.issued_on,
          period_from: d.data.period_from || "",
          period_to: d.data.period_to || "",
          description: d.data.description || "",
        });
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Couldn't load invoice details");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, sheetId]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose, busy]);

  if (!open) return null;

  const periodReady = Boolean(form.period_from && form.period_to && form.period_to >= form.period_from);
  const periodPreview = periodReady ? formatPeriod(form.period_from, form.period_to) : "";
  const overrideNum = amountOverride.trim() === "" ? null : Number(amountOverride);
  const overrideBad =
    overrideOpen && overrideNum !== null && (!isFinite(overrideNum) || overrideNum < 0);

  const download = async (inv) => {
    const res = await api.get(`/invoices/${inv.id}/file`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${inv.number}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const generate = async () => {
    setBusy(true);
    setErr(null);
    try {
      const { data } = await api.post(`/sheets/${sheetId}/invoices`, {
        issued_on: form.issued_on || null,
        period_from: form.period_from,
        period_to: form.period_to,
        description: form.description || null,
        amount_override: overrideOpen && overrideNum !== null ? overrideNum : null,
      });
      setInvoices((list) => [data, ...list]);
      await download(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't generate the invoice");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (inv) => {
    setBusy(true);
    setErr(null);
    try {
      await api.delete(`/invoices/${inv.id}`);
      setInvoices((list) => list.filter((i) => i.id !== inv.id));
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't delete the invoice");
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="modal-backdrop" onClick={busy ? undefined : onClose}>
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{ maxWidth: 620 }}
      >
        <h3>Invoice</h3>

        {loading && <div className="muted" style={{ marginTop: 12 }}>Loading…</div>}

        {!loading && defaults && (
          <>
            <p className="muted" style={{ marginTop: 8 }}>
              Billed to <strong>{defaults.client_name || "—"}</strong> for{" "}
              <strong>{defaults.amount_text}</strong>, the total of this page.
              {defaults.identity_name && <> Issued as {defaults.identity_name}.</>}
              {defaults.bank_account_name && <> Paid into {defaults.bank_account_name}.</>}
            </p>

            {defaults.blockers?.length > 0 && (
              <div className="error" style={{ marginTop: 12 }}>
                Can't generate yet:
                <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                  {defaults.blockers.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
                <div style={{ marginTop: 6 }}>
                  Invoice details are set per client on the Users page; bank accounts
                  live in Settings.
                </div>
              </div>
            )}

            {defaults.can_generate && (
              <div style={{ marginTop: 14 }}>
                <div className="row-h" style={{ gap: 12, alignItems: "flex-end" }}>
                  <div style={{ flex: 1 }}>
                    <label className="field">Work period from</label>
                    <input
                      type="date"
                      value={form.period_from}
                      onChange={(e) => setForm((f) => ({ ...f, period_from: e.target.value }))}
                      disabled={busy}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="field">To</label>
                    <input
                      type="date"
                      value={form.period_to}
                      onChange={(e) => setForm((f) => ({ ...f, period_to: e.target.value }))}
                      disabled={busy}
                    />
                  </div>
                  <div style={{ flex: "0 0 170px" }}>
                    <label className="field">Invoice date</label>
                    <input
                      type="date"
                      value={form.issued_on || ""}
                      onChange={(e) => setForm((f) => ({ ...f, issued_on: e.target.value }))}
                      disabled={busy}
                    />
                  </div>
                </div>
                {periodPreview && (
                  <div className="muted-sm" style={{ marginTop: 6 }}>
                    Prints as <strong>{periodPreview}</strong>
                  </div>
                )}
                {!periodReady && form.period_from && form.period_to && (
                  <div className="muted-sm" style={{ marginTop: 6, color: "var(--danger)" }}>
                    The period ends before it starts.
                  </div>
                )}
                <div style={{ marginTop: 10 }}>
                  <label className="field">Description</label>
                  <input
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    disabled={busy}
                  />
                </div>

                <div style={{ marginTop: 12 }}>
                  {!overrideOpen ? (
                    <button
                      className="ghost"
                      onClick={() => setOverrideOpen(true)}
                      disabled={busy}
                    >
                      Bill a different amount…
                    </button>
                  ) : (
                    <>
                      <div className="row-h" style={{ gap: 12, alignItems: "flex-end" }}>
                        <div style={{ flex: "0 0 180px" }}>
                          <label className="field">Amount to bill ({defaults.currency})</label>
                          <input
                            value={amountOverride}
                            onChange={(e) => setAmountOverride(e.target.value)}
                            placeholder={String(defaults.amount)}
                            inputMode="decimal"
                            autoFocus
                            disabled={busy}
                          />
                        </div>
                        <button
                          className="ghost"
                          onClick={() => {
                            setOverrideOpen(false);
                            setAmountOverride("");
                          }}
                          disabled={busy}
                        >
                          Use the page total
                        </button>
                      </div>
                      <div
                        className="muted-sm"
                        style={{ marginTop: 4, color: overrideBad ? "var(--danger)" : undefined }}
                      >
                        {overrideBad
                          ? "That isn't an amount."
                          : `Only this invoice is billed for it. The page total stays ${defaults.amount_text}.`}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {err && <div className="error" style={{ marginTop: 14 }}>{err}</div>}

        {invoices.length > 0 && (
          <div style={{ marginTop: 18 }}>
            <div className="muted-sm" style={{ marginBottom: 6 }}>
              Generated invoices
            </div>
            {/* A list, not a `.simple` table: that one's fixed 50px action
                column stacks the buttons and its cell borders are far too
                heavy inside a modal. */}
            {invoices.map((inv) => (
              <div
                key={inv.id}
                className="row-h"
                style={{
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "10px 0",
                  borderTop: "1px solid var(--line)",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: "var(--font-mono)" }}>{inv.number}</div>
                  <div className="muted-sm">
                    {fmtDate(inv.issued_on)} · {Number(inv.amount).toLocaleString()}{" "}
                    {inv.currency}
                  </div>
                </div>
                <div className="row-h" style={{ gap: 6, flexWrap: "nowrap", flexShrink: 0 }}>
                  <button className="ghost" onClick={() => download(inv)} disabled={busy}>
                    Download
                  </button>
                  <button
                    className="ghost icon"
                    title="Delete this invoice"
                    onClick={() => remove(inv)}
                    disabled={busy}
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="row-h" style={{ justifyContent: "flex-end", marginTop: 20, gap: 8 }}>
          <button type="button" onClick={onClose} disabled={busy}>Close</button>
          <button
            type="button"
            className="primary"
            onClick={generate}
            disabled={busy || loading || !defaults?.can_generate || !periodReady || overrideBad}
          >
            {busy ? "Generating…" : "Generate invoice"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
